import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import MuiBox from '@mui/material/Box';
import Input from '../Input/Input.js';

const getMockStyles = (theme) => ({
    small: { padding: theme.spacing(0.5, 1) },
    medium: { padding: theme.spacing(1.75, 2) },
    large: { padding: theme.spacing(0.75, 2) },
});
const EllipsisWrapper = styled('div', {
    shouldForwardProp: prop => !['placeholderShown', 'error', 'modifiedManually', 'size', 'disabled', 'inactive'].includes(prop),
}) `
  position: absolute;
  top: 0;
  bottom: 0;
  width: auto;
  pointer-events: none;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  ${({ theme, rows, size = 'small' }) => `
    font-family: ${theme.typography[size === 'large' ? 'h2' : 'body1'].fontFamily};
    font-size: ${theme.typography[size === 'large' ? 'h2' : 'body1'].fontSize} ;
    font-weight: ${theme.typography[size === 'large' ? 'h2' : 'body1'].fontWeight};
    line-height: ${size === 'large' ? '30px' : '16px'};
    ${rows ? `-webkit-line-clamp: ${rows};` : ''}
    margin: ${getMockStyles(theme)[size].padding};
  `}
  ${({ theme, placeholderShown, modifiedManually, inactive, error, disabled }) => {
    let color = theme.palette[disabled ? 'N40' : placeholderShown ? 'N30' : 'N10'].main;
    if (error && !disabled) {
        color = theme.palette.error.main;
    }
    if (placeholderShown) {
        return `
        font-style: italic;
        color: ${color};
      `;
    }
    if (inactive || modifiedManually) {
        color = theme.palette[inactive ? 'N20' : 'warning'].main;
    }
    return `color: ${color};`;
}}
`;
const _Input = styled(Input) `
  height: auto !important;
`;
const Root = styled(MuiBox, {
    shouldForwardProp: prop => !['placeholderShown', 'error', 'disabled'].includes(prop),
}) `
  position: relative;
  ${({ theme, disabled }) => !disabled &&
    `
    :hover {
      textarea::placeholder { 
        color: transparent; 
      }
      textarea:focus { 
        color: ${theme.palette.N10.main} !important; 
      }
    }
    :focus-within div:nth-of-type(2) { 
      display: none; 
    }
    :active div:nth-of-type(2) { 
      display: none; 
    }
  `};
  ${({ theme, disabled, error, placeholderShown }) => !error &&
    !disabled &&
    `
    :hover div:nth-of-type(2) {
      color: ${placeholderShown && theme.palette.primary[placeholderShown ? 'dark' : 'main']};
    }
  `}
`;
const MultilineInput = (_a) => {
    var { ref, value = '', placeholder = '', className, inputProps, ['data-testid']: testId } = _a, rest = __rest(_a, ["ref", "value", "placeholder", "className", "inputProps", 'data-testid']);
    const placeholderShown = !value && !!placeholder;
    const internalInputProps = useMemo(() => (Object.assign(Object.assign({}, inputProps), { ['data-testid']: testId })), [inputProps, testId]);
    return (jsxs(Root, Object.assign({ className: className, placeholderShown: placeholderShown, error: rest.error, disabled: rest.disabled }, { children: [jsx(_Input, Object.assign({ ref: ref, value: value, placeholder: placeholder, inputProps: internalInputProps }, rest, { multiline: true })), jsx(EllipsisWrapper, Object.assign({ placeholderShown: placeholderShown, rows: rest.rows || rest.maxRows, error: rest.error, modifiedManually: rest.modifiedManually, size: rest.size, disabled: rest.disabled, inactive: rest.inactive, "data-testid": "ellipsis-wrapper" }, { children: value || placeholder }))] })));
};

export { MultilineInput as default };
//# sourceMappingURL=MultilineInput.js.map
